import React, { useEffect, useState } from 'react';
import { Typography, Spin, Empty } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useChatManager } from '../hooks/useChatManager';
import { getChatMessages } from '../services/api/chatService';
import { ChatMessageItem } from '../types';
import MarkdownRenderer from '../components/common/MarkdownRenderer';
import { LoadingOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Chats: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const { chats, currentChatId } = useChatManager();
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current chat details
  const currentChat = chats.find(chat => chat.id === (chatId || currentChatId));

  useEffect(() => {
    const loadChatMessages = async () => {
      const targetChatId = chatId || currentChatId;
      if (!targetChatId) {
        setMessages([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await getChatMessages(targetChatId, 100);
        // Convert API response to ChatMessageItem format
        const convertedMessages: ChatMessageItem[] = response.messages.map(msg => ({
          id: String(msg.message_id),
          chatId: response.chat_id,
          role: msg.role,
          content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content),
          timestamp: msg.created_at
        }));
        setMessages(convertedMessages);
      } catch (err) {
        console.error('❌ Failed to load chat messages:', err);
        setError('Failed to load chat messages');
      } finally {
        setIsLoading(false);
      }
    };

    loadChatMessages();
  }, [chatId, currentChatId]);

  // If no chat selected, show chat list
  if (!chatId && !currentChatId) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#0A0A0B',
        padding: '24px'
      }}>
        <Title level={3} style={{ color: '#FFFFFF', marginBottom: '24px' }}>
          Chat History
        </Title>

        {chats.length === 0 ? (
          <Empty
            description={
              <span style={{ color: '#8A8A8A' }}>
                No conversations yet. Start a new chat to begin!
              </span>
            }
            style={{ marginTop: '100px' }}
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {chats.map((chat) => (
              <motion.div
                key={chat.id}
                style={{
                  padding: '16px',
                  background: '#1A1A1B',
                  borderRadius: '8px',
                  border: '1px solid #2c2f2f',
                  cursor: 'pointer'
                }}
                whileHover={{ background: '#2E2E2C' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(`/chats/${chat.id}`)}
              >
                <div style={{ color: '#FFFFFF', fontSize: '15px', fontWeight: '500', marginBottom: '4px' }}>
                  {chat.title}
                </div>
                {chat.lastMessage && (
                  <div style={{
                    color: '#8A8A8A',
                    fontSize: '13px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {chat.lastMessage}
                  </div>
                )}
                <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
                  {new Date(chat.updatedAt).toLocaleString()}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Show individual chat messages
  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: '#0A0A0B',
      padding: '24px'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid #2c2f2f'
      }}>
        <Title level={3} style={{ color: '#FFFFFF', margin: 0 }}>
          {currentChat?.title || 'Chat'}
        </Title>
        {currentChat && (
          <Text style={{ color: '#8A8A8A', fontSize: '13px' }}>
            {new Date(currentChat.updatedAt).toLocaleString()}
          </Text>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px'
          }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#2563EB' }} spin />} />
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', color: '#F44336', padding: '40px' }}>
            {error}
          </div>
        ) : messages.length === 0 ? (
          <Empty
            description={
              <span style={{ color: '#8A8A8A' }}>
                No messages in this conversation
              </span>
            }
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <div style={{
                  color: message.role === 'user' ? '#FFFFFF' : '#2563EB',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {message.role === 'user' ? 'You' : 'AI Assistant'}
                </div>
                <div style={{
                  background: message.role === 'user' ? '#1A1A1B' : '#0F1419',
                  border: `1px solid ${message.role === 'user' ? '#2c2f2f' : '#2563EB30'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  <MarkdownRenderer content={message.content} />
                </div>
                {message.table && (
                  <div style={{
                    marginTop: '12px',
                    border: '1px solid #2c2f2f',
                    borderRadius: '8px',
                    overflow: 'auto',
                    background: '#1A1A1B',
                    padding: '16px'
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: message.table }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Chats;
